import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Mail, Lock, Loader2, MailCheck, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth, type AuthModalMode } from "@/contexts/AuthContext";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: AuthModalMode;
  initialEmail?: string;
  notice?: string;
}

// What the modal shows after a form went through.
type Done = { kind: "signup" | "forgot"; email: string } | null;

const MIN_PASSWORD_LENGTH = 8;

const TITLES: Record<AuthModalMode, string> = {
  login: "Willkommen zurück",
  signup: "Konto erstellen",
  forgot: "Passwort vergessen",
};

const SUBTITLES: Record<AuthModalMode, string> = {
  login: "Melden Sie sich an, um Ihre gespeicherten Chats wiederzufinden.",
  signup:
    "Mit einem kostenlosen Konto finden Sie Ihre Chats und Studienanfragen jederzeit wieder, auch auf anderen Geräten.",
  forgot:
    "Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link, mit dem Sie ein neues Passwort festlegen.",
};

const inputClass =
  "w-full pl-10 pr-3 py-2.5 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-60";

function Field({
  label,
  icon,
  hint,
  children,
}: {
  label: string;
  icon: ReactNode;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-foreground mb-1">{label}</span>
      <span className="relative block">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {icon}
        </span>
        {children}
      </span>
      {hint && <span className="block text-xs text-muted-foreground mt-1">{hint}</span>}
    </label>
  );
}

export function AuthModal({
  isOpen,
  onClose,
  mode: requestedMode,
  initialEmail,
  notice,
}: AuthModalProps) {
  const { login, register, requestPasswordReset } = useAuth();
  const [mode, setMode] = useState<AuthModalMode>(requestedMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  // Separate from the terms: storing chats that may hold health data needs
  // explicit consent (Art. 9 Abs. 2 lit. a DSGVO, privacy page section 3).
  const [acceptedHealthData, setAcceptedHealthData] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState<Done>(null);

  // Each opening starts with a clean form in the requested mode.
  useEffect(() => {
    if (!isOpen) return;
    setMode(requestedMode);
    setEmail(initialEmail ?? "");
    setPassword("");
    setFirstName("");
    setAcceptedPrivacy(false);
    setAcceptedHealthData(false);
    setError("");
    setDone(null);
  }, [isOpen, requestedMode, initialEmail]);

  const switchMode = (next: AuthModalMode) => {
    setMode(next);
    setError("");
    setPassword("");
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    const trimmedEmail = email.trim();

    if (mode === "signup") {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setError(`Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
        return;
      }
      if (!acceptedPrivacy) {
        setError("Bitte stimmen Sie den Datenschutzhinweisen und Nutzungsbedingungen zu.");
        return;
      }
      if (!acceptedHealthData) {
        setError("Bitte willigen Sie in die Speicherung Ihrer Chats im Konto ein.");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (mode === "login") {
        const result = await login(trimmedEmail, password);
        if (result.success) {
          onClose();
        } else {
          // A 403 email_not_verified comes with its own detail, and the
          // backend has sent a new confirmation mail.
          setError(result.error);
        }
      } else if (mode === "signup") {
        const result = await register({
          email: trimmedEmail,
          password,
          firstName: firstName.trim() || undefined,
        });
        if (result.success) {
          setDone({ kind: "signup", email: trimmedEmail });
        } else {
          setError(result.error);
        }
      } else {
        const result = await requestPasswordReset(trimmedEmail);
        if (result.success) {
          setDone({ kind: "forgot", email: trimmedEmail });
        } else {
          setError(result.error);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitLabel =
    mode === "login" ? "Anmelden" : mode === "signup" ? "Konto erstellen" : "Link senden";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="auth-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-60"
            onClick={handleClose}
          />

          <div className="fixed inset-0 z-70 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              key="auth-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="auth-modal-title"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-md bg-(--viewport-bg) rounded-2xl shadow-xl border border-border max-h-[90vh] overflow-y-auto pointer-events-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 id="auth-modal-title" className="text-2xl font-bold text-foreground">
                    {done?.kind === "signup"
                      ? "Fast geschafft"
                      : done?.kind === "forgot"
                        ? "E-Mail unterwegs"
                        : TITLES[mode]}
                  </h2>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    aria-label="Schließen"
                  >
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>

                {done ? (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 rounded-xl bg-green-50 border border-green-200 p-4">
                      <div className="shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <MailCheck className="h-5 w-5 text-green-600" />
                      </div>
                      {done.kind === "signup" ? (
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            Bitte bestätigen Sie Ihre E-Mail-Adresse
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            Wir haben eine E-Mail an <strong>{done.email}</strong> gesendet.
                            Öffnen Sie den Link darin, um Ihr Konto zu aktivieren. Danach
                            können Sie sich anmelden.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            Prüfen Sie Ihr Postfach
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            Wenn zu <strong>{done.email}</strong> ein Konto besteht, haben wir
                            Ihnen einen Link zum Zurücksetzen des Passworts gesendet.
                          </p>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-background hover:bg-muted text-foreground border border-border shadow-xs transition-colors"
                    >
                      Schließen
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mb-5">{SUBTITLES[mode]}</p>

                    {notice && mode === "login" && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">{notice}</p>
                      </div>
                    )}

                    {error && (
                      <div
                        role="alert"
                        className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
                      >
                        <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                      {mode === "signup" && (
                        <Field label="Vorname (optional)" icon={<User className="w-4 h-4" />}>
                          <input
                            type="text"
                            autoComplete="given-name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className={inputClass}
                            placeholder="Max"
                            disabled={isSubmitting}
                          />
                        </Field>
                      )}

                      <Field label="E-Mail" icon={<Mail className="w-4 h-4" />}>
                        <input
                          type="email"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={inputClass}
                          placeholder="max@beispiel.de"
                          required
                          disabled={isSubmitting}
                        />
                      </Field>

                      {mode !== "forgot" && (
                        <Field
                          label="Passwort"
                          icon={<Lock className="w-4 h-4" />}
                          hint={
                            mode === "signup"
                              ? `Mindestens ${MIN_PASSWORD_LENGTH} Zeichen`
                              : undefined
                          }
                        >
                          <input
                            type="password"
                            autoComplete={mode === "signup" ? "new-password" : "current-password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={inputClass}
                            placeholder="••••••••"
                            minLength={mode === "signup" ? MIN_PASSWORD_LENGTH : undefined}
                            required
                            disabled={isSubmitting}
                          />
                        </Field>
                      )}

                      {mode === "login" && (
                        <div className="text-right -mt-2">
                          <button
                            type="button"
                            onClick={() => switchMode("forgot")}
                            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Passwort vergessen?
                          </button>
                        </div>
                      )}

                      {mode === "signup" && (
                        <label className="flex items-start gap-2 text-sm text-foreground">
                          <input
                            type="checkbox"
                            checked={acceptedPrivacy}
                            onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-border accent-primary-600"
                            required
                            disabled={isSubmitting}
                          />
                          <span>
                            Ich habe die{" "}
                            <a
                              href="/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-700 underline"
                            >
                              Datenschutzhinweise
                            </a>{" "}
                            gelesen und stimme den{" "}
                            <a
                              href="/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-700 underline"
                            >
                              Nutzungsbedingungen
                            </a>{" "}
                            zu.
                          </span>
                        </label>
                      )}

                      {mode === "signup" && (
                        <label className="flex items-start gap-2 text-sm text-foreground">
                          <input
                            type="checkbox"
                            checked={acceptedHealthData}
                            onChange={(e) => setAcceptedHealthData(e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-border accent-primary-600"
                            required
                            disabled={isSubmitting}
                          />
                          <span>
                            Ich willige ausdrücklich ein, dass meine Chats, die
                            Gesundheitsdaten enthalten können, in meinem Konto
                            gespeichert werden. Ich kann die Einwilligung jederzeit
                            widerrufen, indem ich mein Konto lösche.
                          </span>
                        </label>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={cn(
                          "w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg shadow-xs transition-all text-white",
                          isSubmitting
                            ? "bg-primary-400 cursor-wait"
                            : "bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 hover:shadow-md",
                        )}
                      >
                        {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        <span>{isSubmitting ? "Bitte warten..." : submitLabel}</span>
                      </button>
                    </form>

                    <div className="mt-6 text-center text-sm text-muted-foreground">
                      {mode === "login" && (
                        <p>
                          Noch kein Konto?
                          <button
                            type="button"
                            onClick={() => switchMode("signup")}
                            className="ml-1 text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Registrieren
                          </button>
                        </p>
                      )}
                      {mode === "signup" && (
                        <p>
                          Bereits ein Konto?
                          <button
                            type="button"
                            onClick={() => switchMode("login")}
                            className="ml-1 text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Anmelden
                          </button>
                        </p>
                      )}
                      {mode === "forgot" && (
                        <button
                          type="button"
                          onClick={() => switchMode("login")}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Zurück zur Anmeldung
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
